/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import { getTransformObject } from '~/common/photos/transforms'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { Distance } from '~/utility/geo/Distance'
import {
  CampsiteDetails,
  PhotoResultUrl,
  PitchType,
  PitchTypePhoto,
  PitchTypePhotoUrl,
  PrimaryPhotos,
  QueryParams,
} from './types'

interface ResponsePhoto {
  caption: string
  url: {
    master_image: string
  }
  titleAttribution: string
}

type ResponsePrimaryPhotos = Record<string, ResponsePhoto>

export class AjaxCampsiteDetailsRepository {
  defaults: {
    defaultAdultsCount?: number
  } = {}

  constructor({ defaults }) {
    if (!defaults.defaultAdultsCount) {
      throw new Error(
        "AjaxCampsiteDetailsRepository missing default 'defaultAdultsCount' please pass to constructor",
      )
    }

    this.defaults = defaults
  }

  async get(
    slug: string,
    langCode: string,
    queryParams?: QueryParams,
  ): Promise<CampsiteDetails | void> {
    // TODO: Add proper type definitions for API response to avoid 'any' usage

    const response: any = await $fetch(this.getURL(slug, langCode))
    return {
      ...response,
      ...this.mapPitchTypesToCheapestPrice(response.pitchtypes),
      id: response.id.toString(),
      attractions: {
        ...response.attractions,
        waterQuality: this.mapWaterQuality(response.attractions.waterQuality),
      },
      nearbyTransport: response.nearbyTransport || [],
      breadcrumbs: response.breadcrumbs,
      cancellationPolicyNeedsTranslation:
        !!response.cancellationPolicyNeedsTranslation,
      descriptionNeedsTranslation: !!response.descriptionNeedsTranslation,
      faq: response.faq ? this.parseFAQ(response.faq, queryParams) : undefined,
      nearbyActivitiesNeedsTranslation:
        !!response.nearbyActivitiesNeedsTranslation,
      noticesNeedsTranslation: !!response.noticesNeedsTranslation,
      pitchtypes: response.pitchtypes.map((pitchtype): PitchType => {
        const leadPrice = pitchtype.leadPrice
        const leadPriceWithExcluded = pitchtype.leadPriceWithExcluded
        let excludedPrice: {
          currency: string
          amount: number
          amountMinor: number
        } | null = null

        if (
          leadPriceWithExcluded &&
          leadPrice &&
          leadPrice.amount !== leadPriceWithExcluded.amount
        ) {
          excludedPrice = {
            currency: leadPrice.currency, // Assuming currency is same
            amount: leadPriceWithExcluded.amount - leadPrice.amount,
            amountMinor:
              leadPriceWithExcluded.amountMinor - leadPrice.amountMinor,
          }
        }
        return {
          ...pitchtype,
          photos: this.mapPitchTypePhotos(pitchtype.photos),
          excludedPrice,
        }
      }),
      primaryPhoto: response.primaryPhoto
        ? {
            caption: response.primaryPhoto?.caption || '',
            url: getTransformObject<PhotoResultUrl>(
              'campsitePrimaryPhotoMaster',
              response.primaryPhoto.url.master_image,
            ),
            titleAttribution: response.primaryPhoto?.titleAttribution || '',
          }
        : null,
      primaryPhotos: this.mapPrimaryPhotos(response.primaryPhotos),
      campingArrivalTimeFrom: response.campingArrivalTimeFrom,
      campingArrivalTimeTo: response.campingArrivalTimeTo,
      campingDepartTimeFrom: response.campingDepartTimeFrom,
      onsiteArrivalTimeFrom: response.onsiteArrivalTimeFrom,
      onsiteArrivalTimeTo: response.onsiteArrivalTimeTo,
      onsiteDepartTimeFrom: response.onsiteDepartTimeFrom,
      warnings: response.covidWarnings || [],
    }
  }

  private parseFAQ(content: string, queryParams?: QueryParams) {
    const found = content.match(/href="(.*?)"/g)
    if (!found?.length) {
      return content
    }
    const paramsToRemove = ['pitchtype', 'sa', 'ved', 'review']
    let newContent = content
    for (const match of found) {
      const path = match.replace('href=', '').replace(/"*/g, '')
      const internalUrl =
        process.env.NUXT_AXIOS_BASE_URL || 'http://pitchupcom-app:8000'
      const url = new URL(path, internalUrl)
      const params = this.getSearchParams(queryParams)
      paramsToRemove.forEach((param) => params.delete(param))
      url.search = params.toString()
      const href = `href="${url.toString().replace(internalUrl || '', '')}"`
      newContent = newContent.replace(match, href)
    }
    return newContent
  }

  private getSearchParams(queryParams?: QueryParams) {
    const params = new URLSearchParams()
    if (!queryParams) {
      return params
    }
    for (const k in queryParams) {
      if (Array.isArray(queryParams[k])) {
        for (const i of Object.keys(queryParams[k])) {
          params.append(k, queryParams[k][i] || '')
        }
      } else {
        params.append(k, (queryParams[k] as string | null) || '')
      }
    }
    return params
  }

  private mapPitchTypePhotos(photos: ResponsePhoto[]): PitchTypePhoto[] {
    return photos.map((photo) => ({
      ...photo,
      url: getTransformObject<PitchTypePhotoUrl>(
        'pitchType',
        photo.url.master_image,
      ),
      titleAttribution: photo.titleAttribution || '',
    }))
  }

  private mapPitchTypesToCheapestPrice(pitchtypes: PitchType[] = []) {
    return {
      leadPriceNights:
        (pitchtypes.length && pitchtypes[0].leadPriceNights) || 1,
      leadPriceAdults:
        (pitchtypes.length && pitchtypes[0].personsIncluded) ||
        this.defaults.defaultAdultsCount,
    }
  }

  private mapPrimaryPhotos(
    primaryPhotos: ResponsePrimaryPhotos,
  ): PrimaryPhotos {
    return Object.entries(primaryPhotos).reduce(
      (accumulator, [category, photo]) => {
        accumulator[category] = {
          ...photo,
          url: getTransformObject<PhotoResultUrl>(
            'campsitePrimaryPhotoMaster',
            photo.url.master_image,
          ),
        }
        return accumulator
      },
      {},
    )
  }

  private mapWaterQuality(waterQuality) {
    return waterQuality.map((wq) => {
      return {
        ...wq,
        distance: this.convertToDistanceObject(wq.distance),
      }
    })
  }

  private getURL(slug: string, langCode: string) {
    return getLangFallbackUrl(langCode, `/_/campsite-details/${slug}/`)
  }

  private convertToDistanceObject(distanceInMeters: number) {
    return Distance.new({ km: distanceInMeters / 1000 })
  }
}
